import React from "react";
import classNames from "classnames";
import { fromJS, Map } from "immutable";
import _ from "lodash";

import PropertyMaps from "../../constants/PropertyMaps";
import StyleUtils from "../../utils/StyleUtils";
import AppConstants from "@/constants/AppConstants";
import ImmutableUtils from "@/utils/ImmutableUtils";
import { gatherProps, inheritProp } from "@/utils/Merge/MergeUtils.mjs";
import { ELEMENT_BACKGROUND_FILL_TYPE, SHADOW_STYLE } from "@/constants/MergeConstants";

const HOVER_FILL_PROPS = PropertyMaps.ELEMENT_FILL_PROPS.map((propName) => `hover.${propName}`);
const HOVER_PROP_NAMES = PropertyMaps.STYLE_PROPS_THEME_BUTTONS.filter(prop => prop.startsWith("hover."));

const getGradientStyle = (elemProps, theme) =>
  elemProps.get("fillType") === "gradient"
    ? {
        ...StyleUtils.getGradientStyle(elemProps, theme),
        backgroundOrigin: "border-box",
        backgroundColor: "transparent",
      }
    : {};

const anchorFlexMap = {
  center: ["center", "center"],
  tl: ["flex-start", "flex-start"],
  tc: ["center", "flex-start"],
  tr: ["flex-end", "flex-start"],
  cl: ["flex-start", "center"],
  cc: ["center", "center"],
  cr: ["flex-end", "center"],
  bl: ["flex-start", "flex-end"],
  bc: ["center", "flex-end"],
  br: ["flex-end", "flex-end"],
};

const getThemeHoverProps = (theme, elemStyle, layout) => {
  const themeStyle = theme.getIn(["styles", elemStyle], fromJS({}));
  return themeStyle.getIn([layout, AppConstants.BUTTON_STATE_HOVER], Map());
};

const getTextProps = (element, layout, propKeys) =>
  (gatherProps(element, layout, propKeys, true) ?? Map()).get("text") ?? Map();

const getThemeTextProps = (themeStyle, layout, propKeys) =>
  (gatherProps(themeStyle, layout, propKeys, true) ?? Map()).get("text") ?? Map();

  const getHoverProps = (element, layout, propKeys) =>
  (gatherProps(element, layout, propKeys, true) ?? Map()).get("hover") ?? Map();

const mergeHoverProps = (baseProps, theme, elemStyle, layout, elemProps, hoverProps, propType) => {
  const themeStyle = theme.getIn(["styles", elemStyle], fromJS({}));
  const themeHoverProps = getThemeHoverProps(theme, elemStyle, layout);

  if (propType === "button") {
    const fillProps =
      (gatherProps(themeStyle, layout, [ELEMENT_BACKGROUND_FILL_TYPE], true) ?? Map())
        .merge(elemProps?.filter(ImmutableUtils.keyIn(PropertyMaps.ELEMENT_FILL_PROPS)))
        .merge(gatherProps(themeStyle, layout, HOVER_FILL_PROPS, false).get("hover") ?? Map())
        .merge(hoverProps?.filter(ImmutableUtils.keyIn(PropertyMaps.ELEMENT_FILL_PROPS))) ?? Map();

    const hoverPropsWithTheme = themeHoverProps.merge(hoverProps.merge(fillProps));
    return baseProps.merge(hoverPropsWithTheme);
  }

  if (propType === "text") {
    const hoverTextProps = getThemeTextProps(
      themeStyle,
      layout,
      PropertyMaps.STYLE_PROPS_BUTTON_TEXT,
    );
    const hoverTextPropsFromTheme = themeHoverProps.get("text") ?? Map();

    return baseProps
      .merge(hoverTextProps)
      .merge(hoverTextPropsFromTheme)
      .merge(hoverProps?.get("text") ?? Map());
  }

  return baseProps;
};

const calculateButtonStyles = (buttonProps, theme, anchor, elemStyle, layout) => {
  const styleDefaults = {
    width: "100%",
    height: "100%",
    minHeight: "fit-content",
  };

  const inlineStyle = StyleUtils.getStyle(PropertyMaps.STYLE_PROPS_INLINE, buttonProps, theme);
  const gradientStyle = getGradientStyle(buttonProps, theme);
  const style = { ...styleDefaults, ...inlineStyle, ...gradientStyle };
  const [anchorJustify, anchorAlign] = anchorFlexMap[anchor];

  style.borderRadius = "inherit";

  const padding = buttonProps.get("padding");
  if (buttonProps.getIn(["shadow", "style"])) {
    style.boxShadow = "inherit";
  }
  style.display = "flex";
  style.outline = "none";
  style.justifyContent = anchorJustify;
  style.alignItems = anchorAlign;
  if (padding) {
    style.padding = padding;
  }
  style.overflow = "hidden";
  style.textOverflow = "ellipsis";

  return style;
};

const calculateTextStyles = (textProps, theme, elemStyle) => {
  let style = {};
  if (textProps) {
    const textPropsKeys = Object.keys(textProps?.toJS());

    textPropsKeys.forEach((key) => {
      const textProp = textProps.get(key);
      if (textProp && textProp !== "style") {
        style[PropertyMaps.SHOWIT_TO_REACT_PROP_MAP[key]] = textProp;
      }
    });
  }

  const styleClass = StyleUtils.getStyleClass(elemStyle);
  const classes = classNames(styleClass);

  const textInlineStyle = StyleUtils.getStyle(PropertyMaps.STYLE_PROPS_INLINE, textProps, theme);

  style = _.merge(style, textInlineStyle);

  style.overflow = "hidden";
  style.textOverflow = "ellipsis";
  style.whiteSpace = "nowrap";

  if (textProps) style = _.merge(style, StyleUtils.getShadowStyle(textProps, theme, "text"));

  return { textStyle: style, textClasses: classes };
};

const Button = ({
  elemProps,
  element,
  id,
  theme,
  layout,
  editing,
  label,
  anchor = "center",
  elemStyle = "primary",
  buttonState,
}) => {
  let buttonProps = gatherProps(element, layout, PropertyMaps.STYLE_PROPS_THEME_BUTTONS, true);
  const shadowStyle = inheritProp(element, layout, SHADOW_STYLE, theme);
  buttonProps = buttonProps.setIn(["shadow", "style"], shadowStyle);

  const hoverProps = getHoverProps(element, layout, HOVER_PROP_NAMES);
  let textProps = getTextProps(element, layout, PropertyMaps.STYLE_PROPS_BUTTON_TEXT);

  if (buttonState === "hover") {
    buttonProps = mergeHoverProps(
      buttonProps,
      theme,
      elemStyle,
      layout,
      elemProps,
      hoverProps,
      "button",
    );
    textProps = mergeHoverProps(textProps, theme, elemStyle, layout, elemProps, hoverProps, "text");
  }

  const style = calculateButtonStyles(buttonProps, theme, anchor, elemStyle, layout);
  const { textStyle, textClasses } = calculateTextStyles(textProps, theme, elemStyle);

  return (
    <button id={id} className={`si-style-button-${elemStyle}`} style={style} type="button">
      {!editing && (
        <span
          style={textStyle}
          className={textClasses}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: label }}
        />
      )}
    </button>
  );
};

export { calculateButtonStyles, calculateTextStyles, mergeHoverProps };
export default Button;
